import React, { createContext, useContext } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { PifListItem } from "../../pages/supervision/PostInterviewForm/types";

export interface PostIFPdfContextValue {
  /** The id currently downloading, or null when idle - see usePostIfPdfDownload. */
  downloadingId: number | null;
  onDownload: (postIfId: number, ref?: string | null) => void;
}

/**
 * Why a context and not cell props: the same reason as PostIFCoordinatorContext - PostIFTable's
 * columns are captured ONCE by useResizableColumns, so a renderCell closing over `downloadingId`
 * would never see it change and the row spinner would never show.
 */
export const PostIFPdfContext = createContext<PostIFPdfContextValue>({
  downloadingId: null,
  onDownload: () => undefined,
});

interface PostIFPdfActionProps {
  row: PifListItem;
}

/**
 * Epic 83470 - the list row's Download PDF action, in every row and every status. Spins in the
 * clicked row only; every row's PDF action is disabled until that download finishes.
 */
const PostIFPdfAction: React.FC<PostIFPdfActionProps> = ({ row }) => {
  const { downloadingId, onDownload } = useContext(PostIFPdfContext);
  const postIfId = Number(row.id);

  return (
    <GridActionsCellItem
      icon={
        downloadingId === postIfId ? (
          <CircularProgress size={16} />
        ) : (
          <Tooltip title="Download PDF" placement="top">
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </Tooltip>
        )
      }
      label="Download PDF"
      disabled={downloadingId !== null}
      onClick={() => onDownload(postIfId, row.refNo)}
      data-testid={`postif-row-downloadpdf-${row.id}`}
    />
  );
};

export default PostIFPdfAction;
